import type { CellKey, GridCoord, Vec2 } from '../types/core';

export const TILE_SIZE = 1;

export function cellKey(coord: GridCoord): CellKey {
  return `${coord.x}:${coord.z}` as CellKey;
}

export function sameCoord(a: GridCoord, b: GridCoord): boolean {
  return a.x === b.x && a.z === b.z;
}

export function isInside(coord: GridCoord, size: number): boolean {
  return coord.x >= 0 && coord.z >= 0 && coord.x < size && coord.z < size;
}

export function coordToWorld(coord: GridCoord, size: number): Vec2 {
  const half = size / 2;
  return {
    x: (coord.x - half + 0.5) * TILE_SIZE,
    z: (coord.z - half + 0.5) * TILE_SIZE
  };
}

export function worldToCoord(position: Vec2, size: number): GridCoord {
  const half = size / 2;
  return {
    x: Math.max(0, Math.min(size - 1, Math.floor(position.x / TILE_SIZE + half))),
    z: Math.max(0, Math.min(size - 1, Math.floor(position.z / TILE_SIZE + half)))
  };
}

export function rectCoords(start: GridCoord, end: GridCoord): GridCoord[] {
  const minX = Math.min(start.x, end.x);
  const maxX = Math.max(start.x, end.x);
  const minZ = Math.min(start.z, end.z);
  const maxZ = Math.max(start.z, end.z);
  const coords: GridCoord[] = [];

  for (let x = minX; x <= maxX; x += 1) {
    for (let z = minZ; z <= maxZ; z += 1) {
      coords.push({ x, z });
    }
  }

  return coords;
}

export function neighbours(coord: GridCoord): GridCoord[] {
  return [
    { x: coord.x + 1, z: coord.z },
    { x: coord.x - 1, z: coord.z },
    { x: coord.x, z: coord.z + 1 },
    { x: coord.x, z: coord.z - 1 }
  ];
}

export function coordDistance(a: GridCoord, b: GridCoord): number {
  return Math.abs(a.x - b.x) + Math.abs(a.z - b.z);
}

export function averageCoord(coords: readonly GridCoord[]): GridCoord {
  if (coords.length === 0) {
    return { x: 0, z: 0 };
  }

  const total = coords.reduce(
    (sum, coord) => ({
      x: sum.x + coord.x,
      z: sum.z + coord.z
    }),
    { x: 0, z: 0 }
  );

  return {
    x: total.x / coords.length,
    z: total.z / coords.length
  };
}

export function uniqueCoords(coords: readonly GridCoord[]): GridCoord[] {
  const seen = new Set<CellKey>();
  const unique: GridCoord[] = [];

  for (const coord of coords) {
    const key = cellKey(coord);
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(coord);
    }
  }

  return unique;
}
